import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Camera, X, CheckCircle, RefreshCw } from 'lucide-react';

import { ToolPageHeader } from '../components/tools/ToolPageHeader';
import { ErrorBanner } from '../components/tools/ErrorBanner';
import { SuccessBanner } from '../components/tools/SuccessBanner';
import { Button } from '../components/ui';
import apiService from '../services/apiService';
import type { PartDetails } from '../services/types';

type Step = 'lookup' | 'confirm' | 'capture' | 'sending' | 'done';

interface StagedPhoto {
  file: File;
  previewUrl: string;
}

export const PictureProcessorPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [partNumInput, setPartNumInput] = useState(
    searchParams.get('partNum') ?? ''
  );
  const [step, setStep] = useState<Step>('lookup');
  const [partDetails, setPartDetails] = useState<PartDetails | null>(null);
  const [stagedPhotos, setStagedPhotos] = useState<StagedPhoto[]>([]);
  const [sendingIndex, setSendingIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLooking, setIsLooking] = useState(false);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // ── Part lookup ──────────────────────────────────────────────────────────
  const handleLookup = async (autoConfirm: boolean = false) => {
    const partNum = partNumInput.trim();
    if (!partNum) return;

    setError(null);
    setIsLooking(true);

    try {
      const details = await apiService.getPart(partNum);
      setPartDetails(details);
      // Caller already verified the part on the previous page — skip the
      // confirm step and drop the user straight into capture.
      if (autoConfirm) {
        setStagedPhotos([]);
        setStep('capture');
      } else {
        setStep('confirm');
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('pictureProcessor.lookupError')
      );
    } finally {
      setIsLooking(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleLookup();
  };

  // ── Auto-lookup when arriving from Part Dashboard via ?partNum= ───────────
  useEffect(() => {
    if (searchParams.get('partNum')) {
      handleLookup(searchParams.get('autoConfirm') === '1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only — partNumInput is already pre-filled from the query param

  // ── Part confirmation ─────────────────────────────────────────────────────
  const handleConfirm = () => {
    setStagedPhotos([]);
    setStep('capture');
  };

  const handleWrongPart = () => {
    setPartDetails(null);
    setStep('lookup');
  };

  // ── Photo staging ────────────────────────────────────────────────────────
  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const newPhotos: StagedPhoto[] = Array.from(files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setStagedPhotos((prev) => [...prev, ...newPhotos]);
  };

  const removePhoto = (index: number) => {
    setStagedPhotos((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  // ── Send photos ──────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!partDetails || stagedPhotos.length === 0) return;

    setStep('sending');
    setError(null);

    for (let i = 0; i < stagedPhotos.length; i++) {
      setSendingIndex(i + 1);
      try {
        await apiService.submitPicture(
          stagedPhotos[i].file,
          partDetails.erpPartNum
        );
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('pictureProcessor.lookupError')
        );
        setStep('capture');
        return;
      }
    }

    // Clean up object URLs
    stagedPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));

    setSuccessMessage(
      t('pictureProcessor.successMessage', {
        count: stagedPhotos.length,
        partNumber: partDetails.part,
      })
    );
    setStagedPhotos([]);
    setStep('done');
  };

  const handleAddMore = () => {
    setSuccessMessage(null);
    setStep('capture');
  };

  const handleStartOver = () => {
    setSuccessMessage(null);
    setPartDetails(null);
    setPartNumInput('');
    setStagedPhotos([]);
    setStep('lookup');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-lg mx-auto">
        <ToolPageHeader
          icon={Camera}
          title={t('pictureProcessor.title')}
          description={t('pictureProcessor.description')}
        />

        <ErrorBanner error={error} onDismiss={() => setError(null)} />
        <SuccessBanner
          message={successMessage}
          onDismiss={() => setSuccessMessage(null)}
        />

        {/* ── Step: Lookup ─────────────────────────────────────────────── */}
        {step === 'lookup' && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <label
              htmlFor="partNum"
              className="block text-sm font-medium text-gray-700"
            >
              {t('pictureProcessor.partNumberLabel')}
            </label>
            <input
              id="partNum"
              type="text"
              value={partNumInput}
              onChange={(e) => setPartNumInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="123456"
              className="w-full text-lg border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="off"
              autoCapitalize="characters"
            />
            <Button
              onClick={() => handleLookup()}
              disabled={!partNumInput.trim() || isLooking}
              className="w-full py-3 text-base"
            >
              {isLooking
                ? t('common.loading')
                : t('pictureProcessor.lookupButton')}
            </Button>
          </div>
        )}

        {/* ── Step: Confirm ─────────────────────────────────────────────── */}
        {step === 'confirm' && partDetails && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              {t('pictureProcessor.confirmPart')}
            </p>
            <div className="space-y-1">
              <p className="text-xl font-bold text-gray-900">
                {partDetails.part}
              </p>
              <p className="text-gray-600">{partDetails.descriptionFR}</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleConfirm} className="flex-1 py-3">
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('common.confirm')}
              </Button>
              <Button
                variant="outline"
                onClick={handleWrongPart}
                className="flex-1 py-3"
              >
                {t('pictureProcessor.wrongPart')}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step: Capture ─────────────────────────────────────────────── */}
        {step === 'capture' && partDetails && (
          <div className="space-y-4">
            {/* Part badge */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <p className="text-sm font-medium text-blue-800">
                {partDetails.part}
              </p>
              <p className="text-xs text-blue-600">
                {partDetails.descriptionFR}
              </p>
            </div>

            {/* Photo buttons */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <Button
                onClick={() => cameraInputRef.current?.click()}
                className="w-full py-4 text-base"
              >
                <Camera className="w-5 h-5 mr-2" />
                {t('pictureProcessor.takePhoto')}
              </Button>
              <Button
                variant="outline"
                onClick={() => galleryInputRef.current?.click()}
                className="w-full py-4 text-base"
              >
                {t('pictureProcessor.addFromGallery')}
              </Button>

              {/* Hidden file inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                onClick={(e) => ((e.target as HTMLInputElement).value = '')}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                onClick={(e) => ((e.target as HTMLInputElement).value = '')}
              />
            </div>

            {/* Thumbnail grid */}
            {stagedPhotos.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex flex-wrap gap-3">
                  {stagedPhotos.map((photo, index) => (
                    <div key={index} className="relative">
                      <img
                        src={photo.previewUrl}
                        alt={`${index + 1}`}
                        className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-600"
                        aria-label={`Remove photo ${index + 1}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Send button */}
            <Button
              onClick={handleSend}
              disabled={stagedPhotos.length === 0}
              className="w-full py-4 text-base"
            >
              {t('pictureProcessor.sendPhotos', { count: stagedPhotos.length })}
            </Button>
          </div>
        )}

        {/* ── Step: Sending ─────────────────────────────────────────────── */}
        {step === 'sending' && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center space-y-4">
            <div className="flex justify-center">
              <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
            <p className="text-gray-700">
              {t('pictureProcessor.sending', {
                current: sendingIndex,
                total: stagedPhotos.length,
              })}
            </p>
          </div>
        )}

        {/* ── Step: Done ────────────────────────────────────────────────── */}
        {step === 'done' && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <Button
              onClick={handleAddMore}
              variant="outline"
              className="w-full py-3"
            >
              {t('pictureProcessor.addMorePhotos')}
            </Button>
            <Button onClick={handleStartOver} className="w-full py-3">
              {t('pictureProcessor.startOver')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
